import { useState, useRef, useCallback } from 'react';
import toolkitService from '../../services/ToolkitService';
import useServerJob from './useServerJob';

// Batch runner for Toolkit screens that process a parsed file row by row against OPERA Cloud — the desktop app's
// "Process All / Pause / Resume / Process one" loop, shared so each screen only supplies `processRow`.
//
// ONE ROW PER REQUEST, IN SEQUENCE. The toolkit lambda runs at reserved concurrency 1 so the OHIP rate limit holds;
// firing rows in parallel would only queue them at the gateway (and 429 the overflow). Sequential also makes Pause
// mean what it says: the row in flight finishes, nothing after it starts.
//
// processRow(row, opts) resolves to the fields to merge onto the row (at least `status`: success | skipped | failed).
// A row already success / skipped is not re-run by Process All — exactly as the desktop.
//
// OR, so the same rows can also run ON THE SERVER (useServerJob — "Run on server"), a screen gives the two halves:
//   request(row, opts)    → { area, op, args, side? } — the one toolkit call for the row (area = ar | ledger |
//                           property | groups | notes | udfs, as toolkitService); or { result: fields } when the
//                           row is decided without a call (e.g. its account is not in Opera Cloud)
//   interpret(result, row) → the fields to merge, from that call's result (default: the result as it is)
//   ctx                    the project, for the calls
//   server                 { screen, title, keyOf(row, i), save?(row), afterItem?, tool? } — enables Run on server.
//                           keyOf names a row stably (account no, room, …); save + afterItem keep the screen's stored
//                           resume state current from the server, as onRowDone does here.

export const DONE_STATUSES = new Set(['success', 'skipped']);

// The row as it came from the file: without the grid's own state (steps, status, message) and any result fields the
// screen added (`extra`), so what is sent to the server is the v5 record and nothing else.
const ROW_STATE = ['steps', 'status', 'errorMessage'];
export const sourceRow = (row, extra = []) =>
    Object.fromEntries(Object.entries(row).filter(([k]) => !ROW_STATE.includes(k) && !extra.includes(k)));

const useToolkitBatch = ({ processRow: givenProcessRow, request, interpret, ctx, server, onRowDone } = {}) => {
    const [rows, setRows] = useState([]);
    const [running, setRunning] = useState(false);
    const [current, setCurrent] = useState(-1);
    const pausedRef = useRef(false);
    const rowsRef = useRef([]);

    const load = useCallback((list) => {
        const next = (list || []).map((r) => ({ status: 'pending', errorMessage: null, steps: [], ...r }));
        rowsRef.current = next;
        setRows(next);
    }, []);

    const patch = useCallback((index, fields) => {
        const next = [...rowsRef.current];
        next[index] = { ...next[index], ...fields };
        rowsRef.current = next;
        setRows(next);
        return next[index];
    }, []);

    // The browser's run of one row, from request / interpret when the screen gives them.
    const derived = useCallback(async (row, opts = {}) => {
        const q = request(row, opts);
        if (q.result) return q.result;
        const { data } = await toolkitService[q.area](ctx, q.op, q.args);
        return interpret ? interpret(data.result, row) : data.result;
    }, [request, interpret, ctx]);
    const processRow = givenProcessRow || derived;

    // opts reach processRow — e.g. { bypassDateCheck: true } for a row-level "Post anyway".
    const runOne = useCallback(async (index, opts = {}) => {
        patch(index, { status: 'processing', errorMessage: null });
        let fields;
        try {
            fields = await processRow(rowsRef.current[index], opts);
        } catch (err) {
            fields = { status: 'failed',
                errorMessage: err?.response?.data?.message || err?.response?.data?.error?.message || err?.message || 'Request failed' };
        }
        const row = patch(index, fields);
        if (onRowDone) await Promise.resolve(onRowDone(row, index)).catch(() => {});
        return row;
    }, [patch, processRow, onRowDone]);

    const runAll = useCallback(async () => {
        pausedRef.current = false;
        setRunning(true);
        for (let i = 0; i < rowsRef.current.length; i++) {
            if (pausedRef.current) break;
            if (DONE_STATUSES.has(rowsRef.current[i].status)) continue;
            setCurrent(i);
            await runOne(i);
        }
        setCurrent(-1);
        setRunning(false);
    }, [runOne]);

    const pause = useCallback(() => { pausedRef.current = true; }, []);

    const counts = rows.reduce((c, r) => { c[r.status] = (c[r.status] || 0) + 1; return c; }, {});
    const remaining = rows.filter((r) => !DONE_STATUSES.has(r.status)).length;

    const job = useServerJob({ ctx, server, rowsRef, patch, request, interpret, doneStatuses: DONE_STATUSES });

    return { rows, load, patch, runOne, runAll, pause, running, current, counts, remaining, job };
};

export default useToolkitBatch;
